import React, { Fragment, useEffect, useRef, useState } from 'react';
import {
  Typography,
  Button,
  Form,
  message,
  Input,
  Modal,
  Switch,
  Radio,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import Axios from 'axios';
import { fetchCompanyRule } from '../../../utils/companyRule';
const { Title, Text } = Typography;

// 補足説明文のスタイル（既存項目の見出しと揃えた控えめな説明）
const NOTE_STYLE = { fontSize: 'var(--text-sm)' };

// 事前点検モーダル内の数値表示。0 でない値は注意を引くため danger 色で強調する
const renderCount = (n) => {
  const num = Number(n) || 0;
  return num > 0 ? <Text type="danger">{num}</Text> : <Text>{num}</Text>;
};

// 系列会社マスタの state 用行から、送信用の { _id?, name, active } を作る（一時キーは除去）
const toCompanyPayload = (rows) =>
  (rows || []).map((c) => {
    const row = { name: (c.name || '').trim(), active: c.active !== false };
    if (c._id) row._id = c._id;
    return row;
  });

const MakeGroupPage = (props) => {
  // 新規作成時は「系列会社なし・マスタ空」を初期値とする
  const [value, setValue] = useState({ has_affiliates: false, company: [] });
  const [mode, setMode] = useState('new');
  const [originalGroupCode, setOriginalGroupCode] = useState(null);
  const [prechecking, setPrechecking] = useState(false);
  // 系列会社一覧の取得結果（GET /api/group/:code/company-list）。編集モードかつ系列会社ありのときだけ取得する。
  // source==='org'（有効な確定組織表で系列会社を管理）なら系列会社マスタの編集をロックする
  const [companyListInfo, setCompanyListInfo] = useState(null);
  // 追加行の一時キー採番用（React key 専用。送信時には除去する）
  const tmpKeySeq = useRef(0);
  const gInfo = props.location.state;
  const { user } = props;
  // 編集前の「系列会社あり」フラグ（OFF→ON 切替時の事前点検要否の判定に使う）
  const originalHasAffiliates = !!(gInfo && gInfo.has_affiliates);

  useEffect(() => {
    if (!user.userData) return null;
    if (gInfo._id) {
      setMode('edit');
      setOriginalGroupCode(gInfo.group_code);
      setValue({
        _id: gInfo._id,
        group_code: gInfo.group_code,
        group_name: gInfo.group_name,
        group_description: gInfo.group_description,
        no_login_fg: gInfo.no_login_fg,
        mfa: gInfo.mfa,
        has_affiliates: !!gInfo.has_affiliates,
        company: (gInfo.company || []).map(({ _id, name, active }) => ({
          _id,
          name,
          active: active !== false,
          _key: _id,
        })),
      });
      // 系列会社ありグループは、系列会社が組織表で管理されているかを確認する（失敗時は従来どおり編集可）
      if (gInfo.has_affiliates) {
        fetchCompanyRule(gInfo.group_code).then((info) => setCompanyListInfo(info));
      }
    }
  }, [user]);

  // 系列会社マスタの編集ロック（組織表で系列会社を管理しているグループ）。
  // ロック中は保存時に company を送らない（サーバーは組織表管理のグループで company の変更を 400 にするため）
  const companyLocked =
    mode === 'edit' && !!companyListInfo && companyListInfo.source === 'org';

  const onChangeGroupCode = (e) => {
    setValue({ ...value, group_code: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeGroupName = (e) => {
    setValue({ ...value, group_name: e.currentTarget.value });
    // console.log(value);
  };
  const onChangeGroupDescription = (e) => {
    setValue({ ...value, group_description: e.currentTarget.value });
    // console.log(value);
  };

  const onChangeSwitch = (checked) => {
    // console.log(`switch to ${checked}`);
    setValue({ ...value, no_login_fg: checked });
  };

  const onChangeMfa = (e) => {
    setValue({ ...value, mfa: e.target.value });
  };

  // 事前点検結果を確認モーダルで表示し、「ON にする」が押されたときだけ ON にする
  const showPrecheckConfirm = (r) => {
    const problemTotal =
      (Number(r.users_company_unregistered) || 0) +
      (Number(r.org_versions_without_company_node) || 0) +
      (Number(r.e3_values_unresolvable) || 0);
    const samples = (r.unregistered_samples || []).slice(0, 10);
    // ユーザーに登録されている会社名のうちマスタ未登録のもの（空欄を除く）。ON 時にマスタ編集欄へ取り込む
    const importable = (r.unregistered_companies || [])
      .map((c) => (c && c.name ? String(c.name).trim() : ''))
      .filter((n) => n !== '');
    const lineStyle = { margin: 'var(--space-1) 0' };
    Modal.confirm({
      title: 'グループ会社構成を ON にする前の事前点検',
      content: (
        <div>
          <p style={lineStyle}>
            系列会社マスタの登録数:{' '}
            {Number(r.company_count) > 0 ? (
              <Text>{Number(r.company_count)}</Text>
            ) : (
              <Text type="danger">0</Text>
            )}{' '}
            社
            <br />
            <Text type="secondary" style={NOTE_STYLE}>
              （保存済みの内容で点検しています）
            </Text>
          </p>
          <p style={lineStyle}>
            会社名がマスタに無いユーザー:{' '}
            {renderCount(r.users_company_unregistered)} 名 / 全{' '}
            {Number(r.users_total) || 0} 名
          </p>
          {samples.length > 0 && (
            <ul style={{ margin: 'var(--space-1) 0', paddingLeft: 'var(--space-5)' }}>
              {samples.map((s, i) => (
                <li key={i} style={NOTE_STYLE}>
                  {s.company ? s.company : '（空欄）'}: {Number(s.count) || 0}
                </li>
              ))}
            </ul>
          )}
          <p style={lineStyle}>
            系列会社ノードの無い確定済み組織表:{' '}
            {renderCount(r.org_versions_without_company_node)} 版 / 全{' '}
            {Number(r.org_versions_confirmed) || 0} 版
          </p>
          <p style={lineStyle}>
            解釈できないシーズン管理者の所属指定:{' '}
            {renderCount(r.e3_values_unresolvable)} 件 / 全{' '}
            {Number(r.e3_values_total) || 0} 件
          </p>
          {problemTotal === 0 && (
            <p style={lineStyle}>問題は見つかりませんでした。</p>
          )}
          {importable.length > 0 && (
            <p style={{ ...lineStyle, marginTop: 'var(--space-3)' }}>
              <Text style={NOTE_STYLE}>
                「ON にする」を押すと、ユーザーに登録されている会社名 {importable.length} 社を
                下の系列会社マスタの編集欄に取り込みます（保存前に名称の修正・削除ができます）。
              </Text>
            </p>
          )}
          <p style={{ ...lineStyle, marginTop: 'var(--space-3)' }}>
            <Text type="secondary" style={NOTE_STYLE}>
              0 でない項目がある状態で ON
              にすると、名簿アップロードの検証エラーや絞り込みの不一致が発生します。データ整理後に
              ON にしてください。
            </Text>
          </p>
        </div>
      ),
      okText: 'ON にする',
      cancelText: 'キャンセル',
      // 危険側（ON にする）を既定 CTA にしないため、キャンセル側を primary にする
      okButtonProps: { type: 'default' },
      cancelButtonProps: { type: 'primary' },
      onOk: () => {
        // 検討事項（2026-10-07）: ユーザーの会社名を系列会社マスタの編集欄へ自動で取り込む
        // （既にある名称は重複させない。保存は従来どおり「保存」ボタンで行う）
        setValue((prev) => {
          const existing = new Set((prev.company || []).map((c) => (c.name || '').trim()));
          const added = importable
            .filter((n) => !existing.has(n))
            .map((n) => {
              tmpKeySeq.current += 1;
              return { name: n, active: true, _key: `tmp-${tmpKeySeq.current}` };
            });
          return { ...prev, has_affiliates: true, company: [...(prev.company || []), ...added] };
        });
      },
    });
  };

  // 「グループ会社構成（系列会社あり）」スイッチ
  const onChangeHasAffiliates = async (checked) => {
    // ON→OFF はデータを変更しないため確認なしで切り替える
    // 新規作成時、または元から ON のグループは点検不要
    if (!checked || mode !== 'edit' || originalHasAffiliates) {
      setValue({ ...value, has_affiliates: checked });
      return;
    }
    // 編集モードで元の値が OFF のグループを ON にする場合のみ事前点検を行う
    setPrechecking(true);
    try {
      const res = await Axios.get(
        `/api/group/${encodeURIComponent(originalGroupCode)}/affiliate-precheck`
      );
      if (!res.data || !res.data.success) {
        throw new Error('事前点検に失敗しました');
      }
      showPrecheckConfirm(res.data);
    } catch (error) {
      message.error('事前点検に失敗しました');
      console.log({ error });
    } finally {
      setPrechecking(false);
    }
  };

  // 系列会社マスタの行操作
  const updateCompanyRow = (idx, patch) => {
    const company = [...(value.company || [])];
    company[idx] = { ...company[idx], ...patch };
    setValue({ ...value, company });
  };
  const moveCompanyRow = (idx, delta) => {
    const company = [...(value.company || [])];
    const to = idx + delta;
    if (to < 0 || to >= company.length) return;
    [company[idx], company[to]] = [company[to], company[idx]];
    setValue({ ...value, company });
  };
  const removeCompanyRow = (idx) => {
    const company = [...(value.company || [])];
    company.splice(idx, 1);
    setValue({ ...value, company });
  };
  const addCompanyRow = () => {
    tmpKeySeq.current += 1;
    setValue({
      ...value,
      company: [
        ...(value.company || []),
        { name: '', active: true, _key: `tmp-${tmpKeySeq.current}` },
      ],
    });
  };

  // 保存前のクライアント検証（系列会社マスタ）。問題があればエラーを表示して false を返す
  const validateCompany = () => {
    const company = value.company || [];
    const names = company.map((c) => (c.name || '').trim());
    if (names.some((n) => n === '')) {
      message.error('系列会社名が空欄の行があります');
      return false;
    }
    if (new Set(names).size !== names.length) {
      message.error('系列会社名が重複しています');
      return false;
    }
    if (value.has_affiliates && !company.some((c) => c.active !== false)) {
      message.error(
        'グループ会社構成を ON にする場合は、系列会社を 1 社以上登録してください'
      );
      return false;
    }
    return true;
  };

  const doSave = async () => {
    try {
      let res = {};
      if (mode === 'edit') {
        const submitData = {
          ...value,
          company: toCompanyPayload(value.company),
        };
        // 系列会社を組織表で管理しているグループでは company を送らない（変更不可・400 回避）
        if (companyLocked) delete submitData.company;
        // グループコード変更の場合、元のコードを送信
        if (value.group_code !== originalGroupCode) {
          submitData.original_group_code = originalGroupCode;
        }
        res = await Axios.put('/api/group', submitData);
      } else {
        res = await Axios.post('/api/group', {
          ...value,
          company: toCompanyPayload(value.company),
        });
      }
      if (res.data.success) {
        message.success({
          content: 'グループ登録完了！',
          key: 'submit',
          duration: 2,
        });
        setTimeout(() => {
          props.history.push('/grouplist');
        }, 1000);
      } else {
        throw new Error('グループ登録に失敗しました。');
      }
    } catch (error) {
      // サーバーの検証エラー（使用中の系列会社の削除・改名、空欄・重複など）は文言をそのまま表示する
      const serverError =
        error && error.response && error.response.data
          ? error.response.data.error
          : null;
      message.error({
        content:
          typeof serverError === 'string' && serverError
            ? serverError
            : 'グループ登録に失敗しました。',
        key: 'submit',
        duration: typeof serverError === 'string' && serverError ? 5 : 2,
      });
      console.log({ error });
    }
  };

  const onSubmit = async () => {
    // 系列会社マスタの検証（空欄・重複・ON 時の有効件数）。組織表で管理中（ロック）は送信しないため検証しない
    if (!companyLocked && !validateCompany()) return;
    // 編集モードでグループコードが変更された場合、確認ダイアログを表示
    if (mode === 'edit' && value.group_code !== originalGroupCode) {
      Modal.confirm({
        title: '確認',
        content:
          'グループコードを変更すると、他のデータとの連携に問題が発生する可能性があります。変更してもよろしいですか？',
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: async () => {
          await doSave();
        },
      });
    } else {
      await doSave();
    }
  };

  const onCancel = async () => {
    props.history.push('/grouplist');
  };

  return (
    <Fragment>
      <div style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Title level={2}>
            {mode === 'edit' ? 'グループ編集' : 'グループ作成'}
          </Title>
        </div>
        <Form onSubmit={onSubmit}>
          <br />
          <Text strong>■ グループコード：英数大文字（必須）</Text>
          <div
            style={{ display: 'flex', justifyContent: 'space-between' }}
          ></div>
          <Input
            placeholder="グループコード（必須）"
            name="group_code"
            allowClear
            onChange={onChangeGroupCode}
            value={value['group_code']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ グループ名：社名（必須）</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Input
            placeholder="グループ名（社名）"
            name="group_name"
            allowClear
            onChange={onChangeGroupName}
            value={value['group_name']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ グループ説明</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Input
            placeholder="グループ説明"
            name="group_description"
            allowClear
            onChange={onChangeGroupDescription}
            value={value['group_description']}
          />
          <br />
          <br />
          <div>
            <Text strong>■ 「ログイン」しないサーベイ用グループ</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Switch
            checked={value['no_login_fg'] || false}
            onChange={onChangeSwitch}
          />
          <br />
          <br />
          <div>
            <Text strong>■ 多要素認証を実施</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Radio.Group onChange={onChangeMfa} value={value['mfa'] || false}>
            <Radio value={false}>実施しない</Radio>
            <Radio value={true}>実施する</Radio>
          </Radio.Group>
          <br />
          <br />
          <div>
            <Text strong>■ グループ会社構成（系列会社あり）</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <Switch
            checked={!!value['has_affiliates']}
            loading={prechecking}
            onChange={onChangeHasAffiliates}
            aria-label="グループ会社構成（系列会社あり）"
          />
          <div style={{ marginTop: 'var(--space-1)' }}>
            <Text type="secondary" style={NOTE_STYLE}>
              ON にすると、ユーザーの「会社名」は系列会社名として扱われ、下の系列会社マスタに登録された名称のみ入力できるようになります。OFF
              に戻してもデータは変更されません。
            </Text>
          </div>
          <br />
          <div>
            <Text strong>■ 系列会社マスタ</Text>
            <div
              style={{ display: 'flex', justifyContent: 'space-between' }}
            ></div>
          </div>
          <div style={{ marginBottom: 'var(--space-2)' }}>
            {companyLocked ? (
              <Text type="secondary" style={NOTE_STYLE}>
                このグループの系列会社は組織表（有効な確定版「
                {(companyListInfo.version && companyListInfo.version.name) || ''}
                」）で管理されています。系列会社の追加・変更は組織情報管理で行ってください。
              </Text>
            ) : (
              <Text type="secondary" style={NOTE_STYLE}>
                グループ会社構成を ON
                にする前に登録してください。使用中の系列会社は削除・名称変更できません（廃止する場合は「使用する」を
                OFF にしてください）。
              </Text>
            )}
          </div>
          {(value.company || []).map((c, idx, arr) => (
            <div
              key={c._id || c._key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)',
              }}
            >
              <Input
                placeholder="系列会社名"
                aria-label="系列会社名"
                value={c.name}
                disabled={companyLocked}
                onChange={(e) =>
                  updateCompanyRow(idx, { name: e.currentTarget.value })
                }
                style={{ flex: 1 }}
              />
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  whiteSpace: 'nowrap',
                }}
              >
                <Switch
                  size="small"
                  checked={c.active !== false}
                  disabled={companyLocked}
                  onChange={(checked) =>
                    updateCompanyRow(idx, { active: checked })
                  }
                  aria-label="使用する"
                />
                <Text style={NOTE_STYLE}>使用する</Text>
              </span>
              <Button
                icon={<ArrowUpOutlined />}
                aria-label="上へ移動"
                disabled={companyLocked || idx === 0}
                onClick={() => moveCompanyRow(idx, -1)}
              />
              <Button
                icon={<ArrowDownOutlined />}
                aria-label="下へ移動"
                disabled={companyLocked || idx === arr.length - 1}
                onClick={() => moveCompanyRow(idx, 1)}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                aria-label="削除"
                disabled={companyLocked}
                onClick={() => removeCompanyRow(idx)}
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            disabled={companyLocked}
            onClick={addCompanyRow}
          >
            系列会社を追加
          </Button>
          <br />
          <br />
          <Button
            type="primary"
            size="large"
            onClick={onSubmit}
            disabled={false}
          >
            保存
          </Button>
          　
          <Button
            size="large"
            onClick={onCancel}
            disabled={false}
          >
            キャンセル
          </Button>
        </Form>
      </div>
    </Fragment>
  );
};

export default MakeGroupPage;
